import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table'
import { useState } from 'react'

import type { DisbursementLine } from '@/api/reports-types'
import { DataTable, dataTableColumns, type DataTableColumn } from '@/components/data-table'
import { MoneyDisplay } from '@/components/money-display'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatShortDate } from '@/lib/datetime'

import { ReportExportButtons } from './report-export-buttons'
import { resolveBranchLabel, startOfMonth, today } from './constants'
import { BranchFilterField, LoanOfficerFilterField } from './filters'
import { REPORTS_PERMISSIONS, useReportsPermissions } from './permissions'
import { useDisbursementsRegister, useReportsBranchReach } from './queries'
import { ReportHeaderNotice } from './report-header-notice'
import { RestrictedPanel } from './restricted-panel'

const DEFAULT_PAGE_SIZE = 50
const UNSORTED: SortingState = []
const ignoreSortingChange: OnChangeFn<SortingState> = () => undefined

/**
 * The disbursements register: rounds disbursed in a date range, with what actually left the till or
 * the member's account net of fees and any security deposit taken up front.
 */
export function DisbursementsRegisterSection() {
  const permissions = useReportsPermissions()
  const reach = useReportsBranchReach()

  const [fromDate, setFromDate] = useState(() => startOfMonth(today()))
  const [toDate, setToDate] = useState(today)
  const [branchId, setBranchId] = useState<string | null>(null)
  const [loanOfficerId, setLoanOfficerId] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const query = useDisbursementsRegister(
    { fromDate, toDate, branchId, loanOfficerId, page, pageSize },
    permissions.canViewDisbursements && fromDate !== '' && toDate !== '',
  )

  if (!permissions.canViewDisbursements) {
    return (
      <RestrictedPanel what="The disbursements register" permission={REPORTS_PERMISSIONS.portfolioView} />
    )
  }

  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const current: PaginationState = { pageIndex: page - 1, pageSize }
    const next = typeof updater === 'function' ? updater(current) : updater
    setPage(next.pageIndex + 1)
    setPageSize(next.pageSize)
  }

  const helper = dataTableColumns<DisbursementLine>()
  const columns: DataTableColumn<DisbursementLine>[] = helper.columns([
    helper.accessor('loanAccountNumber', {
      header: 'Loan account',
      enableSorting: false,
      cell: (context) => (
        <span className="font-mono text-xs">{context.row.original.loanAccountNumber}</span>
      ),
      meta: { noWrap: true, widthClass: 'w-36', card: 'lead' },
    }),
    helper.accessor('customerName', {
      header: 'Customer',
      enableSorting: false,
      cell: (context) => (
        <span className="font-medium text-heading">{context.row.original.customerName}</span>
      ),
      meta: { card: 'title' },
    }),
    helper.accessor('groupName', {
      header: 'Group',
      enableSorting: false,
      cell: (context) => context.row.original.groupName ?? <span className="text-muted">Individual</span>,
      meta: { widthClass: 'w-36' },
    }),
    helper.accessor('branchId', {
      header: 'Branch',
      enableSorting: false,
      cell: (context) => (
        <span className="text-xs">{resolveBranchLabel(reach.branches, context.row.original.branchId)}</span>
      ),
      meta: { noWrap: true, widthClass: 'w-28' },
    }),
    helper.accessor('loanProductName', {
      header: 'Product',
      enableSorting: false,
      cell: (context) => <span className="text-xs">{context.row.original.loanProductName}</span>,
      meta: { widthClass: 'w-32' },
    }),
    helper.accessor('valueDate', {
      header: 'Value date',
      enableSorting: false,
      cell: (context) => (
        <span className="numeric text-xs tabular-nums">
          {formatShortDate(context.row.original.valueDate)}
        </span>
      ),
      meta: { noWrap: true, widthClass: 'w-28' },
    }),
    helper.accessor('principal', {
      header: 'Principal',
      enableSorting: false,
      cell: (context) => (
        <MoneyDisplay
          value={context.row.original.principal.minor}
          currency={context.row.original.principal.currency}
          symbol={false}
        />
      ),
      meta: { align: 'right', widthClass: 'w-32', cardLabel: 'Principal' },
    }),
    helper.accessor('feesDeducted', {
      header: 'Fees deducted',
      enableSorting: false,
      cell: (context) => (
        <MoneyDisplay
          value={context.row.original.feesDeducted.minor}
          currency={context.row.original.feesDeducted.currency}
          symbol={false}
        />
      ),
      meta: { align: 'right', widthClass: 'w-32', cardLabel: 'Fees deducted' },
    }),
    helper.accessor('securityDepositDeducted', {
      header: 'Deposit taken',
      enableSorting: false,
      cell: (context) => (
        <MoneyDisplay
          value={context.row.original.securityDepositDeducted.minor}
          currency={context.row.original.securityDepositDeducted.currency}
          symbol={false}
        />
      ),
      meta: { align: 'right', widthClass: 'w-32', cardLabel: 'Deposit taken' },
    }),
    helper.accessor('netPaidOut', {
      header: 'Net paid out',
      enableSorting: false,
      cell: (context) => (
        <MoneyDisplay
          value={context.row.original.netPaidOut.minor}
          currency={context.row.original.netPaidOut.currency}
          emphasis="strong"
        />
      ),
      meta: { align: 'right', widthClass: 'w-32', cardLabel: 'Net paid out' },
    }),
  ])

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="grid gap-3 p-4 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label htmlFor="disbursements-from">From</Label>
            <Input
              id="disbursements-from"
              type="date"
              value={fromDate}
              onChange={(event) => {
                setFromDate(event.target.value)
                setPage(1)
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="disbursements-to">To</Label>
            <Input
              id="disbursements-to"
              type="date"
              value={toDate}
              onChange={(event) => {
                setToDate(event.target.value)
                setPage(1)
              }}
            />
          </div>
          <BranchFilterField
            id="disbursements-branch"
            branchId={branchId}
            onChange={(next) => {
              setBranchId(next)
              setLoanOfficerId(null)
              setPage(1)
            }}
          />
          <LoanOfficerFilterField
            id="disbursements-officer"
            branchId={branchId}
            loanOfficerId={loanOfficerId}
            onChange={(next) => {
              setLoanOfficerId(next)
              setPage(1)
            }}
          />
        </CardContent>
      </Card>

      {query.data ? <ReportHeaderNotice header={query.data.header} /> : null}

      <ReportExportButtons
        query={{
          report: 'DisbursementsRegister',
          fromDate,
          toDate,
          branchId,
          loanOfficerId,
          page,
          pageSize,
        }}
      />

      {query.data ? (
        <Card>
          <CardHeader>
            <CardTitle>Totals for this range</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
            <TotalTile label="Principal disbursed" money={query.data.totalPrincipal} />
            <TotalTile label="Fees deducted" money={query.data.totalFeesDeducted} />
            <TotalTile label="Security deposit taken" money={query.data.totalSecurityDepositDeducted} />
            <TotalTile label="Net paid out" money={query.data.totalNetPaidOut} strong />
          </CardContent>
        </Card>
      ) : null}

      <DataTable
        columns={columns}
        data={query.data?.rows}
        rowCount={query.data?.paging.totalRows}
        pagination={{ pageIndex: page - 1, pageSize }}
        onPaginationChange={onPaginationChange}
        sorting={UNSORTED}
        onSortingChange={ignoreSortingChange}
        isLoading={query.isPending}
        isFetching={query.isFetching}
        error={query.isError ? query.error : null}
        onRetry={() => void query.refetch()}
        getRowId={(row) => row.disbursementId}
        emptyTitle="Nothing disbursed"
        emptyDescription="No round was disbursed in this range under these filters."
      />
    </div>
  )
}

function TotalTile({
  label,
  money,
  strong = false,
}: {
  label: string
  money: { minor: number; currency: string }
  strong?: boolean
}) {
  return (
    <div className="rounded-control border border-border-subtle bg-surface p-3">
      <p className="text-2xs text-muted">{label}</p>
      <MoneyDisplay
        value={money.minor}
        currency={money.currency}
        emphasis={strong ? 'strong' : 'normal'}
        className="text-left"
      />
    </div>
  )
}
